import axios from 'axios';
import type {
  PortfolioSummary,
  Position,
  Balance,
  RiskMetrics,
  PositionRisk,
  StrategySignal,
  StrategyPerformance,
  Trade,
  MLPrediction,
  ModelAccuracy,
  MemoryDecision,
  LessonLearned,
  MemorySearchResult,
  Alert,
} from '../types';

// Base API client pointing to the orchestrator which proxies to all services
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Portfolio API ─────────────────────────────────────────────────────────────

export async function fetchPortfolioSummary(): Promise<PortfolioSummary> {
  const { data } = await api.get('/portfolio-manager/summary');
  return data;
}

export async function fetchPositions(): Promise<Position[]> {
  const { data } = await api.get('/portfolio-manager/positions');
  return data.positions ?? [];
}

export async function fetchBalances(): Promise<Balance[]> {
  const { data } = await api.get('/portfolio-manager/balances');
  return data.balances ?? [];
}

// ─── Risk API ──────────────────────────────────────────────────────────────────

export async function fetchRiskMetrics(): Promise<RiskMetrics> {
  const { data } = await api.get('/risk-engine/metrics');
  return data;
}

export async function fetchPositionRisks(): Promise<PositionRisk[]> {
  const { data } = await api.get('/risk-engine/positions');
  return data.positions ?? [];
}

// ─── Strategy API ──────────────────────────────────────────────────────────────

export async function fetchStrategies(): Promise<StrategyPerformance[]> {
  const { data } = await api.get('/strategy-engine/strategies');
  return data.strategies ?? [];
}

export async function fetchStrategySignals(symbol?: string): Promise<StrategySignal[]> {
  const params = symbol ? { symbol } : {};
  const { data } = await api.get('/strategy-engine/signals', { params });
  return data.signals ?? [];
}

// ─── Trades API ────────────────────────────────────────────────────────────────

export async function fetchTrades(limit = 50): Promise<Trade[]> {
  const { data } = await api.get('/execution-engine/trades', { params: { limit } });
  return data.trades ?? [];
}

// ─── ML API ───────────────────────────────────────────────────────────────────

export async function fetchPredictions(): Promise<MLPrediction[]> {
  const { data } = await api.get('/ml-prediction/current');
  return data.predictions ?? [];
}

export async function fetchModelAccuracy(): Promise<ModelAccuracy[]> {
  const { data } = await api.get('/ml-prediction/accuracy');
  return data.models ?? [];
}

// ─── Memory API ───────────────────────────────────────────────────────────────

export async function fetchMemoryStats() {
  const { data } = await api.get('/memory/stats');
  return data;
}

export async function searchMemory(query: string, limit = 5): Promise<MemorySearchResult[]> {
  const { data } = await api.get('/memory/search', { params: { query, limit } });
  return data.results ?? [];
}

export async function fetchLessons(): Promise<LessonLearned[]> {
  const { data } = await api.get('/memory/lessons');
  return data.lessons ?? [];
}

export async function storeDecision(decision: Omit<MemoryDecision, 'id'>): Promise<{ id: string }> {
  const { data } = await api.post('/memory/decision', decision);
  return data;
}

// ─── Market Data API ───────────────────────────────────────────────────────────

export async function fetchMarketData(symbol: string, timeframe = '1h') {
  const { data } = await api.get('/market-data/candles', {
    params: { symbol, timeframe, limit: 100 },
  });
  return data;
}

// ─── Alerts API ────────────────────────────────────────────────────────────────

export async function fetchAlerts(): Promise<Alert[]> {
  const { data } = await api.get('/reporting/alerts');
  return data.alerts ?? [];
}

export async function acknowledgeAlert(id: string): Promise<void> {
  await api.post(`/reporting/alerts/${id}/acknowledge`);
}

// ─── Backtest API ──────────────────────────────────────────────────────────────
export interface BacktestParams {
  symbol: string;
  strategy: string;
  interval: string;
  limit: number;
  initial_capital?: number;
  position_size_pct?: number;
  start_time?: number;
  end_time?: number;
  name?: string;
}

export interface BacktestResult {
  id: number;
  strategy: string;
  strategy_name?: string;
  symbol: string;
  interval: string;
  initial_capital: number;
  final_equity: number;
  total_return: number;
  total_return_pct: number;
  sharpe_ratio: number;
  max_drawdown_pct: number;
  win_rate: number;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  avg_win: number;
  avg_loss: number;
  trades: any[];
  equity_curve: number[];
  created_at: string;
  status: string;
}

export interface BacktestSummary {
  id: number;
  name?: string;
  strategy_name: string;
  symbol: string;
  interval: string;
  status: string;
  total_return_pct: number;
  sharpe_ratio: number;
  max_drawdown_pct: number;
  win_rate: number;
  total_trades: number;
  initial_capital: number;
  final_equity: number;
  created_at: string;
}

export async function runBacktest(params: BacktestParams): Promise<BacktestResult> {
  const { data } = await api.post('/backtesting/run', null, { params });
  return data;
}

export async function listBacktests(): Promise<BacktestSummary[]> {
  const { data } = await api.get('/backtesting');
  return data;
}

export async function getBacktest(id: number): Promise<BacktestResult> {
  const { data } = await api.get(`/backtesting/${id}`);
  return data;
}

export async function getBacktestEquityCurve(id: number): Promise<{ equity_curve: number[]; trades: Trade[] }> {
  const { data } = await api.get(`/backtesting/${id}/equity-curve`);
  return data;
}
